<template>
  <div class="user-form-page">
    <div class="form-hero">
      <p class="form-crumb">Usuarios / {{ form.id ? 'Editar' : 'Nuevo' }}</p>
      <h1 class="form-title">Usuario / {{ form.id ? 'Editar' : 'Nuevo' }}</h1>
      <p class="form-subtitle">
        {{
          form.id
            ? 'Actualiza la información del usuario y su configuración de acceso.'
            : 'Crea un nuevo usuario del sistema. Este usuario no estará asociado a ningún laboratorio ni distribuidor.'
        }}
      </p>
    </div>

    <div class="form-card">
      <v-form @submit.prevent="submit">
        <!-- ===== Información del Usuario ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Información del Usuario</h3>

          <div class="form-grid">
            <div class="form-field">
              <label class="form-label">
                Nombre <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.firstName"
                v-validate="'required'"
                :error-messages="errors.collect('firstName')"
                data-vv-name="firstName"
                placeholder="Ingrese el nombre del usuario"
                append-icon="person_outline"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">
                Apellido <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.lastName"
                v-validate="'required'"
                :error-messages="errors.collect('lastName')"
                data-vv-name="lastName"
                placeholder="Ingrese el apellido del usuario"
                append-icon="badge"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">
                Correo Electrónico <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.email"
                v-validate="'required|email'"
                :error-messages="errors.collect('email')"
                data-vv-name="email"
                placeholder="usuario@dominio.com"
                append-icon="alternate_email"
                type="email"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">
                Tipo de Usuario (Rol) <span class="form-required">*</span>
              </label>
              <v-select
                v-model="form.roleId"
                v-validate="'required'"
                :error-messages="errors.collect('role')"
                :items="roles"
                data-vv-name="role"
                placeholder="Selecciona un rol"
                append-icon="security"
                solo
                flat
                no-data-text="Datos no disponibles"
                class="form-input form-input--select"
              >
                <template v-slot:no-data>
                  <div class="role-no-data">
                    <span class="role-no-data__text">
                      No hay roles disponibles.
                    </span>
                    <v-btn
                      class="btn-secondary"
                      small
                      @click="openCreateRoleFromSearch"
                    >
                      <v-icon left small>add</v-icon>Crear nuevo rol
                    </v-btn>
                  </div>
                </template>
              </v-select>
              <p class="form-hint">
                <v-icon x-small class="form-hint__icon">info</v-icon>
                <span>
                  ¿No encuentras el rol?
                  <a class="form-hint__link" @click="openCreateRoleFromSearch"
                    >Crear nuevo rol</a
                  >
                </span>
              </p>
            </div>

            <div v-if="isTransportistaRole" class="form-field">
              <label class="form-label">
                Transportista <span class="form-required">*</span>
              </label>
              <v-autocomplete
                v-model="form.carrierId"
                v-validate="isTransportistaRole ? 'required' : ''"
                :error-messages="errors.collect('transporter')"
                :items="transporters"
                :loading="loadingTransporters"
                data-vv-name="transporter"
                placeholder="Selecciona el transportista"
                append-icon="local_shipping"
                solo
                flat
                no-data-text="No hay transportistas disponibles"
                class="form-input form-input--select"
              ></v-autocomplete>
              <p class="form-hint">
                <v-icon x-small class="form-hint__icon">info</v-icon>
                <span
                  >El usuario quedará asociado a este transportista como
                  dueño.</span
                >
              </p>
            </div>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Seguridad de Acceso ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Seguridad de Acceso</h3>

          <div class="form-grid">
            <div class="form-field">
              <label class="form-label">
                {{ form.id ? 'Contraseña (opcional)' : 'Contraseña Temporal' }}
                <span v-if="!form.id" class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.password"
                v-validate="form.id ? '' : 'required'"
                :error-messages="errors.collect('password')"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
                data-vv-name="password"
                placeholder="Ingrese la contraseña"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">Confirmar Contraseña</label>
              <v-text-field
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                :append-icon="
                  showConfirmPassword ? 'visibility' : 'visibility_off'
                "
                @click:append="showConfirmPassword = !showConfirmPassword"
                data-vv-name="confirmPassword"
                placeholder="Repita la contraseña"
                solo
                flat
                class="form-input"
              ></v-text-field>
              <p v-if="passwordErrorText" class="form-error-text">
                {{ passwordErrorText }}
              </p>
            </div>
          </div>

          <div v-if="form.id" class="form-action-row">
            <div class="form-action-row__text">
              <p class="form-action-row__title">
                Reset con contraseña por defecto
              </p>
              <p class="form-action-row__help">
                Restablece la contraseña del usuario con el valor por defecto
                definido por el sistema.
              </p>
            </div>
            <v-btn class="btn-secondary" small @click="openResetPasswordDialog">
              <v-icon left small>lock_reset</v-icon>Reset por defecto
            </v-btn>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Estado del Usuario ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Estado del Usuario</h3>

          <div class="form-toggle-row">
            <v-switch
              v-model="form.enabled"
              hide-details
              color="#e5252a"
              class="form-switch"
            ></v-switch>
            <div class="form-toggle-text">
              <p class="form-toggle-label">
                {{ form.enabled ? 'Usuario activo' : 'Usuario inactivo' }}
              </p>
              <p class="form-toggle-help">
                {{
                  form.enabled
                    ? 'El usuario puede iniciar sesión en el sistema.'
                    : 'El usuario está deshabilitado y no puede acceder.'
                }}
              </p>
            </div>
          </div>
        </section>

        <!-- Info nota -->
        <div class="info-note">
          <v-icon small class="info-note__icon">info</v-icon>
          <div>
            <p class="info-note__title">Información importante</p>
            <ul>
              <li>
                La contraseña ingresada se usará para el acceso inicial del
                usuario.
              </li>
              <li>
                Si el usuario olvida su contraseña, deberá solicitar soporte al
                administrador.
              </li>
            </ul>
          </div>
        </div>

        <!-- ===== Footer ===== -->
        <div class="form-footer">
          <v-btn
            class="btn-cancel"
            flat
            @click="$router.push('/usuarios/listado')"
          >
            <v-icon left small>close</v-icon>Cancelar
          </v-btn>
          <v-btn type="submit" class="btn-save" :loading="saving">
            <v-icon left small>save</v-icon>
            {{ form.id ? 'Guardar Cambios' : 'Guardar Usuario' }}
          </v-btn>
        </div>
      </v-form>
    </div>

    <!-- Dialog mensajes -->
    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message
        :key="dialog.key"
        :options="dialog"
        v-on:close="dialog.show = false"
      ></message>
    </v-dialog>

    <!-- Dialog crear rol inline -->
    <v-dialog max-width="520" persistent v-model="roleDialog.show">
      <v-card class="modal-card">
        <div class="modal-header">
          <h3 class="modal-header__title">Crear nuevo rol</h3>
          <p class="modal-header__subtitle">
            Define un nuevo rol que podrás asignar al usuario.
          </p>
        </div>
        <v-card-text class="modal-body">
          <v-form ref="roleForm">
            <div class="form-field">
              <label class="form-label">
                Nombre del rol <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="roleDialog.form.name"
                :rules="[v => !!v || 'El nombre del rol es requerido']"
                placeholder="Ej: Administrador de Sistemas"
                append-icon="security"
                solo
                flat
                class="form-input"
                @input="onRoleNameInput"
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label">
                Código del rol <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="roleDialog.form.code"
                :rules="[v => !!v || 'El código del rol es requerido']"
                placeholder="Ej: ADMIN_SIS"
                append-icon="vpn_key"
                solo
                flat
                class="form-input"
                @input="onRoleCodeInput"
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label">Descripción (opcional)</label>
              <v-text-field
                v-model="roleDialog.form.description"
                placeholder="Describe el propósito del rol"
                append-icon="notes"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>
          </v-form>
        </v-card-text>
        <div class="modal-footer">
          <v-btn class="btn-cancel" flat small @click="closeRoleDialog"
            >Cancelar</v-btn
          >
          <v-btn
            class="btn-save"
            small
            :loading="roleDialog.saving"
            @click="createRoleInline"
          >
            <v-icon left small>save</v-icon>Guardar rol
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog reset password -->
    <v-dialog max-width="560" persistent v-model="resetDialog.show">
      <v-card class="modal-card">
        <div class="modal-header">
          <h3 class="modal-header__title">Reset de contraseña por defecto</h3>
          <p class="modal-header__subtitle">
            Esta acción aplicará la contraseña por defecto al usuario actual.
          </p>
        </div>
        <v-card-text class="modal-body">
          <div class="info-note info-note--warning">
            <v-icon small class="info-note__icon">warning</v-icon>
            <div>
              <p>
                Asegúrate de comunicar la nueva contraseña al usuario de forma
                segura.
              </p>
            </div>
          </div>

          <div v-if="resetDialog.result" class="reset-result">
            <p>
              <span>ID</span><strong>{{ resetDialog.result.id }}</strong>
            </p>
            <p>
              <span>Email</span><strong>{{ resetDialog.result.email }}</strong>
            </p>
            <p>
              <span>Password aplicada</span
              ><strong>{{ resetDialog.result.password || '***' }}</strong>
            </p>
            <p>
              <span>usedDefaultPassword</span
              ><strong>{{ resetDialog.result.usedDefaultPassword }}</strong>
            </p>
          </div>
        </v-card-text>
        <div class="modal-footer">
          <v-btn class="btn-cancel" flat small @click="closeResetPasswordDialog"
            >Cerrar</v-btn
          >
          <v-btn
            class="btn-save"
            small
            :loading="resetDialog.saving"
            @click="resetPasswordToDefault"
          >
            <v-icon left small>lock_reset</v-icon>Ejecutar reset
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/api/index'
import message from './../dialog/Message'

export default {
  components: { message },
  props: {
    form: {
      type: Object,
      default() {
        return {
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          roleId: '',
          carrierId: '',
          enabled: 1,
        }
      },
    },
  },
  data() {
    return {
      saving: false,
      showPassword: false,
      showConfirmPassword: false,
      confirmPassword: '',
      passwordErrorText: '',
      roleSearch: '',
      roles: [],
      roleCodeById: {},
      transporters: [],
      loadingTransporters: false,
      roleDialog: {
        show: false,
        saving: false,
        codeTouched: false,
        form: {
          name: '',
          code: '',
          description: '',
          isActive: true,
        },
      },
      resetDialog: {
        show: false,
        saving: false,
        result: null,
      },
      dialog: {
        show: false,
        key: 0,
      },
    }
  },
  created() {
    this.loadTransporters()
    this.refreshRoles().then(() => {
      if ('id' in this.$route.params) {
        this.$store
          .dispatch('users/getById', { id: this.$route.params.id })
          .then(response => {
            if (response) {
              this.form = Object.assign(this.form, {
                id: response.id,
                firstName: response.firstName,
                lastName: response.lastName,
                email: response.email,
                roleId: response.roleId,
                carrierId: response.carrierId || '',
                enabled: response.enabled,
              })
            } else {
              this.$router.push('/usuarios/listado')
            }
          })
      }
    })
  },
  computed: {
    showCreateRoleButton() {
      const term = (this.roleSearch || '').trim().toLowerCase()
      if (!term) return false
      return !this.roles.some(role => (role.text || '').toLowerCase() === term)
    },
    isTransportistaRole() {
      return this.roleCodeById[this.form.roleId] === 'TRA'
    },
  },
  watch: {
    'form.roleId'() {
      if (!this.isTransportistaRole) {
        this.form.carrierId = ''
      }
    },
  },
  methods: {
    openCreateRoleFromSearch() {
      this.roleDialog.form.name = (this.roleSearch || '').trim()
      if (this.roleDialog.form.name) {
        this.roleDialog.form.code = this.generateRoleCode(
          this.roleDialog.form.name
        )
      }
      this.roleDialog.codeTouched = false
      this.roleDialog.show = true
    },
    onRoleNameInput(value) {
      if (!this.roleDialog.codeTouched) {
        this.roleDialog.form.code = this.generateRoleCode(value)
      }
    },
    onRoleCodeInput(value) {
      this.roleDialog.codeTouched = true
      this.roleDialog.form.code = (value || '')
        .toUpperCase()
        .replace(/\s+/g, '_')
    },
    generateRoleCode(name) {
      return (name || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 20)
    },
    closeRoleDialog() {
      this.roleDialog.show = false
      this.roleDialog.saving = false
      this.roleDialog.codeTouched = false
      this.roleDialog.form = {
        name: '',
        code: '',
        description: '',
        isActive: true,
      }
    },
    notifyInline(messageText, color = 'primary') {
      this.dialog = {
        message: messageText,
        color,
        show: true,
        key: this.dialog.key + 1,
      }
    },
    async refreshRoles() {
      const response = await this.$store.dispatch('users/getRoles')
      const userRoles = Array.isArray(response)
        ? response
        : Array.isArray(response && response.items)
        ? response.items
        : []

      const hiddenCodes = ['LAB', 'DIST']
      this.roleCodeById = userRoles.reduce((map, role) => {
        map[role.id] = (role.code || '').toUpperCase()
        return map
      }, {})

      this.roles = userRoles
        .filter(role => !hiddenCodes.includes((role.code || '').toUpperCase()))
        .map(role => ({
          text: role.name,
          value: role.id,
        }))
      return userRoles
    },
    async loadTransporters() {
      this.loadingTransporters = true
      try {
        const response = await Api.Transporter.getBasicList()
        const items = Array.isArray(response)
          ? response
          : Array.isArray(response && response.items)
          ? response.items
          : []
        this.transporters = items.map(transporter => ({
          text: transporter.name,
          value: transporter.id,
        }))
      } catch (error) {
        this.transporters = []
      } finally {
        this.loadingTransporters = false
      }
    },
    async createRoleInline() {
      if (!this.$refs.roleForm.validate()) return
      this.roleDialog.saving = true
      try {
        const payload = {
          name: this.roleDialog.form.name,
          code: this.roleDialog.form.code,
          description: this.roleDialog.form.description,
          isActive: true,
        }
        const response = await Api.Role.save(payload)
        if (
          response &&
          typeof response.status === 'number' &&
          response.status >= 400
        ) {
          throw new Error('error al crear rol')
        }

        const roles = await this.refreshRoles()
        const matchByName = roles.find(
          role => role.name === payload.name || role.code === payload.code
        )
        if (matchByName) {
          this.form.roleId = matchByName.id
        }

        this.closeRoleDialog()
        this.notifyInline(
          'Rol creado correctamente y seleccionado en el formulario.',
          'primary'
        )
      } catch (error) {
        this.notifyInline(
          'No se pudo crear el rol. Verifica nombre/código e intenta de nuevo.',
          'error'
        )
      } finally {
        this.roleDialog.saving = false
      }
    },
    openResetPasswordDialog() {
      this.resetDialog.show = true
      this.resetDialog.result = null
    },
    closeResetPasswordDialog() {
      this.resetDialog.show = false
      this.resetDialog.saving = false
      this.resetDialog.result = null
    },
    async resetPasswordToDefault() {
      if (!this.form.id) return
      this.resetDialog.saving = true
      try {
        const response = await Api.User.resetPassword(this.form.id, {
          useDefaultPassword: true,
        })

        if (
          response &&
          typeof response.status === 'number' &&
          response.status >= 400
        ) {
          throw new Error('Error de reset')
        }

        const payload = response.data || response
        this.resetDialog.result = {
          id: payload.id,
          email: payload.email,
          password: payload.password,
          usedDefaultPassword: payload.usedDefaultPassword,
        }

        this.form.password = payload.password || ''
        this.confirmPassword = payload.password || ''
        this.notifyInline(
          'Contraseña restablecida con valor por defecto.',
          'primary'
        )
      } catch (error) {
        this.notifyInline(
          'No se pudo restablecer la contraseña por defecto.',
          'error'
        )
      } finally {
        this.resetDialog.saving = false
      }
    },
    submit() {
      this.passwordErrorText = ''

      const needsConfirmation =
        !this.form.id || this.form.password || this.confirmPassword
      if (needsConfirmation && !this.confirmPassword) {
        this.passwordErrorText = 'Debes confirmar la contraseña.'
        return
      }

      if (
        (this.form.password || this.confirmPassword) &&
        this.form.password !== this.confirmPassword
      ) {
        this.passwordErrorText = 'Las contraseñas no coinciden.'
        return
      }

      this.$validator.validateAll().then(result => {
        if (!result) return

        this.saving = true
        this.$store
          .dispatch('users/save', this.form)
          .then(response => {
            this.saving = false
            if (response.status === 200) {
              if (this.form.id) {
                this.dialog = {
                  message: 'Los datos fueron actualizados exitosamente',
                  color: 'primary',
                  show: true,
                  key: this.dialog.key + 1,
                }
              } else {
                this.$router.push('/usuarios/listado?success=true')
              }
              this.confirmPassword = ''
            } else {
              this.dialog = {
                message:
                  (response.data && response.data.error) ||
                  'Ocurrió un error al guardar',
                color: 'error',
                show: true,
                key: this.dialog.key + 1,
              }
            }
          })
          .catch(() => {
            this.saving = false
            this.dialog = {
              message: 'No se pudo conectar al servidor. Intenta más tarde.',
              color: 'error',
              show: true,
              key: this.dialog.key + 1,
            }
          })
      })
    },
  },
}
</script>

<style scoped>
.user-form-page {
  padding: 24px 28px 40px;
  color: #172133;
  background: #f9fafb;
  min-height: 100%;
}

/* === Hero === */
.form-hero {
  margin-bottom: 24px;
}

.form-crumb {
  margin: 0 0 6px;
  font-size: 12.5px;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.form-title {
  margin: 0 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #172133;
  letter-spacing: -0.01em;
}

.form-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
  max-width: 720px;
}

/* === Card === */
.form-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 14px rgba(15, 23, 42, 0.05);
}

.form-section__title {
  margin: 0 0 18px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 10px;
}

.form-section__title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 32px;
  height: 2px;
  background: #e5252a;
  border-radius: 1px;
}

.form-divider {
  height: 1px;
  background: #eef2f7;
  margin: 24px 0;
}

/* === Grid === */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 18px;
}

.form-field--full {
  grid-column: 1 / -1;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}

.form-required {
  color: #e5252a;
  font-weight: 700;
}

.form-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-hint__icon {
  color: #94a3b8 !important;
  font-size: 13px !important;
}

.form-hint__link {
  color: #254f87;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
  margin-left: 3px;
}

.form-hint__link:hover {
  color: #e5252a;
}

.form-error-text {
  margin: 6px 2px 0;
  color: #e5252a;
  font-size: 11.5px;
  font-weight: 600;
}

/* === Action row (reset password etc) === */
.form-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fafbfd;
}

.form-action-row__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #172133;
}

.form-action-row__help {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: #64748b;
}

/* === Toggle === */
.form-toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 6px 0;
}

.form-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.form-toggle-text {
  flex: 1;
  padding-top: 6px;
}

.form-toggle-label {
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: #172133;
}

.form-toggle-help {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: #64748b;
}

/* === Info note === */
.info-note {
  display: flex;
  gap: 10px;
  margin-top: 24px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fafbfd;
}

.info-note__icon {
  color: #254f87 !important;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-note__title {
  margin: 0 0 6px;
  font-size: 12.5px;
  font-weight: 700;
  color: #172133;
}

.info-note ul {
  margin: 0;
  padding-left: 16px;
}

.info-note li {
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.5;
}

.info-note--warning .info-note__icon {
  color: #c79a2b !important;
}

.info-note--warning {
  border-color: rgba(199, 154, 43, 0.4);
  background: rgba(199, 154, 43, 0.06);
}

.info-note--warning p {
  margin: 0;
  font-size: 12.5px;
  color: #172133;
}

/* === Footer === */
.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #eef2f7;
}

.btn-cancel {
  border: 1px solid #e2e8f0 !important;
  background: transparent !important;
  color: #475569 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0.01em;
  font-weight: 500 !important;
  height: 42px !important;
  padding: 0 18px !important;
  box-shadow: none !important;
}

.btn-cancel:hover {
  background: #f8fafc !important;
  color: #172133 !important;
  border-color: #cbd5e1 !important;
}

.btn-save {
  background: #e5252a !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0.01em;
  font-weight: 600 !important;
  height: 42px !important;
  padding: 0 22px !important;
  box-shadow: 0 1px 2px rgba(229, 37, 42, 0.12),
    0 4px 12px rgba(229, 37, 42, 0.2) !important;
  transition: all 0.15s ease !important;
}

.btn-save:hover {
  background: #c91e23 !important;
  box-shadow: 0 2px 4px rgba(229, 37, 42, 0.18),
    0 6px 16px rgba(229, 37, 42, 0.28) !important;
  transform: translateY(-1px);
}

.btn-secondary {
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: #254f87 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.btn-secondary:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.role-no-data {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.role-no-data__text {
  font-size: 12px;
  color: #64748b;
}

@media (max-width: 720px) {
  .user-form-page {
    padding: 18px 14px 28px;
  }
  .form-card {
    padding: 18px 16px;
  }
  .form-title {
    font-size: 22px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-footer {
    flex-direction: column-reverse;
  }
  .btn-cancel,
  .btn-save {
    width: 100%;
    justify-content: center;
  }
  .form-action-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
/* === Inputs y modal (no scoped: v-text-field, v-dialog se renderizan con clases globales / portales) === */
.user-form-page
  .form-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.modal-card
  .form-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
  min-height: 44px !important;
  padding: 0 14px !important;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.user-form-page
  .form-input.v-text-field.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot,
.modal-card
  .form-input.v-text-field.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot {
  border-color: #9ca3af !important;
}

.user-form-page
  .form-input.v-text-field.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot,
.modal-card
  .form-input.v-text-field.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.04) !important;
}

.user-form-page .form-input.error--text .v-input__control > .v-input__slot,
.modal-card .form-input.error--text .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}

.user-form-page .form-input input,
.user-form-page .form-input textarea,
.modal-card .form-input input,
.modal-card .form-input textarea {
  color: #172133 !important;
  font-size: 14px !important;
  caret-color: #172133 !important;
}

.user-form-page .form-input input::placeholder,
.user-form-page .form-input textarea::placeholder,
.modal-card .form-input input::placeholder,
.modal-card .form-input textarea::placeholder {
  color: #94a3b8 !important;
  opacity: 1;
}

.user-form-page .form-input .v-input__icon .v-icon,
.modal-card .form-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
  font-size: 19px !important;
}

.user-form-page .form-input .v-messages.error--text,
.modal-card .form-input .v-messages.error--text {
  color: #e5252a !important;
  font-size: 11.5px !important;
  margin-top: 4px;
  padding-left: 4px;
}

/* Switch estilo iOS */
.user-form-page .form-switch .v-input--switch__track {
  background: #e5e7eb !important;
  opacity: 1 !important;
  height: 22px !important;
  width: 40px !important;
  border-radius: 11px !important;
  top: calc(50% - 11px) !important;
  transition: background-color 0.25s ease;
}

.user-form-page .form-switch.v-input--is-label-active .v-input--switch__track {
  background: #e5252a !important;
  opacity: 1 !important;
}

.user-form-page .form-switch .v-input--switch__thumb {
  background: #ffffff !important;
  width: 18px !important;
  height: 18px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.06) !important;
  top: calc(50% - 9px) !important;
}

/* === Modal === */
.modal-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.modal-header {
  padding: 18px 22px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-header__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #172133;
  letter-spacing: 0.01em;
}

.modal-header__subtitle {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: #64748b;
}

.modal-body {
  padding: 18px 22px !important;
}

.modal-body .form-field + .form-field {
  margin-top: 14px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px 18px;
  border-top: 1px solid #eef2f7;
}

.reset-result {
  margin-top: 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  padding: 12px 14px;
}

.reset-result p {
  margin: 0 0 6px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
}

.reset-result p:last-child {
  margin-bottom: 0;
}

.reset-result span {
  color: #64748b;
  font-weight: 500;
}

.reset-result strong {
  color: #172133;
  font-weight: 700;
}
</style>
